import { type NextPage } from "next";
import { Navbar } from "../features/home";
import Image from "next/image";
import signinBackground from "../assets/signinbackgroundcompressed.jpg";
import Link from "next/link";
import { useState } from "react";
import { useRouter } from 'next/router';
import axios from "axios";
import { User } from "@/types/types";

const ChangePassword: NextPage = () => {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setIsLoading(true);

    try {
      // First, verify user credentials
      const signInResponse = await axios({
        method: 'post',
        url: 'https://api.getspect.ai/v1/login',
        headers: { 'Content-Type': 'application/json' },
        data: {
          email,
          password: oldPassword
        },
        withCredentials: true
      });

      const user: User = signInResponse.data;
      if (!user) {
        setError("Invalid email or old password. Please check your credentials and try again.");
        return;
      }

      // Then, change password
      const changePasswordResponse = await fetch("https://api.getspect.ai/v3/registration/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          password: newPassword,
        }),
      });

      if (!changePasswordResponse.ok) {
        const errorData = await changePasswordResponse.json().catch(() => ({}));
        setError(errorData.message || "Failed to change password. Please try again later.");
        return;
      }

      // Show success message and redirect after delay
      setSuccess(true);
      setTimeout(() => {
        router.push("/signin");
      }, 2000);

    } catch (err) {
      console.error("Error changing password: ", err);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || 
                 "Network error occurred. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex h-screen w-full justify-center items-center font-inter">
      <div className="absolute inset-0">
        <Image
          src={signinBackground}
          alt="Background"
          fill
          priority
          className="object-cover"
          placeholder="blur"
        />
      </div>
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="z-10 flex flex-col w-full max-w-md mx-4 space-y-4 bg-white border p-6 rounded shadow-lg">
        <Link href="/">
          <h1 className="text-4xl font-bold tracking-tight text-darkBlue text-center mt-4">
            Spect{" "}
            <span className="bg-gradient-to-r from-rose-400 via-fuchsia-500 to-indigo-500 bg-clip-text text-transparent">
              AI
            </span>
          </h1>
        </Link>
        <h2 className="text-black text-center text-base pt-2">
          To change your password, please enter your email, old password, and new password.
        </h2>
        <div className="flex flex-col space-y-4 justify-center items-center pt-4">
          <form className="w-full" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email-address" className="sr-only">Email address</label>
              <input 
                id="email-address" 
                name="email" 
                type="email" 
                autoComplete="email" 
                required 
                className="w-full h-12 rounded-sm border bg-white p-4 mb-3" 
                placeholder="Email address" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
              />
            </div>
            <div className="relative">
              <label htmlFor="old-password" className="sr-only">Old Password</label>
              <input 
                id="old-password" 
                name="old-password" 
                type={showOldPassword ? "text" : "password"}
                autoComplete="current-password" 
                required 
                className="w-full h-12 rounded-sm border bg-white p-4 mb-3" 
                placeholder="Old Password" 
                value={oldPassword} 
                onChange={e => setOldPassword(e.target.value)} 
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-500"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            <div className="relative">
              <label htmlFor="new-password" className="sr-only">New Password</label>
              <input 
                id="new-password" 
                name="new-password" 
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password" 
                required 
                className="w-full h-12 rounded-sm border bg-white p-4 mb-3" 
                placeholder="New Password" 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-500"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            <div className="mb-6">
              <button 
                type="submit" 
                className="font-normal text-base text-white bg-mainPink hover:brightness-90 rounded-sm py-3 w-full flex justify-center items-center" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'Continue'
                )}
              </button>
              {error && <p className="mt-2 text-center text-sm text-red-600">{error}</p>}
              {success && (
                <p className="mt-2 text-center text-sm text-green-600">
                  Password changed successfully! Redirecting to sign in...
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
